import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Icon } from '@iconify/react';
import { getAssessmentDetails, getAssessmentAnalyticsUsingGemini } from '../../../services/adminService';
import { decodeData } from '../../../utils/encodeAndEncode';

function CourseAnalyticsAssessmentDetails() {
  const { assessmentId, courseId } = useParams();
  const decodedCourseId = courseId ? decodeData(courseId) : null;
  const decodedAssessmentId = assessmentId ? decodeData(assessmentId) : null;
  const navigate = useNavigate();

  const [assessmentDetails, setAssessmentDetails] = useState(null);
  const [recentAttempts, setRecentAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);

  useEffect(() => {
    fetchAssessmentDetails();
  }, [decodedAssessmentId, decodedCourseId]);

  const fetchAssessmentDetails = async () => {
    try {
      setLoading(true);
      const response = await getAssessmentDetails(decodedAssessmentId, decodedCourseId);

      // console.log('Assessment Details Response:', response);

      if (response.success && response.data) {
        setAssessmentDetails(response.data.assessment);
        setRecentAttempts(response.data.recent_attempts || []);
      }
    } catch (error) {
      console.error('Error fetching assessment details:', error);
    } finally {
      setLoading(false);
    }

  };

  const handleGetAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const payload = {
        assessment_id: decodedAssessmentId
      };

      const response = await getAssessmentAnalyticsUsingGemini(payload);
      // console.log('Assessment Analytics Response:', response);

      if (response.success) {
        setAnalyticsData(response);
        setShowAnalyticsModal(true);
      }

    } catch (error) {
      console.error('Error fetching assessment analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center" style={{ minHeight: '400px' }}>
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!assessmentDetails) {
    return (
      <div className="text-center py-5">
        <div className="text-gray-500">Assessment not found</div>
        <button className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg mt-3" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <>
      <div>
      {/* Header with Get Analytics Button */}
      <div className="mb-4">
        <div className="w-full">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                aria-label="Go back"
              >
                <Icon icon="fluent:arrow-left-24-regular" width="20" height="20" />
              </button>
              <h4 className="mb-0 text-2xl font-semibold">Assessment Analytics</h4>
            </div>
            <button
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleGetAnalytics}
              disabled={analyticsLoading}
            >
              {analyticsLoading ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <span>Getting Analytics...</span>
                </>
              ) : (
                <>
                  <Icon icon="mdi:chart-line" width="18" />
                  <span>Get Analytics</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Assessment Overview */}
      <div className="mb-4">
        <div className="w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="border rounded-lg p-3">
                  <div className="text-gray-500 mb-1 text-sm">Total Questions</div>
                  <div className="text-2xl font-semibold mb-0">{assessmentDetails.total_questions || 0}</div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="text-gray-500 mb-1 text-sm">Total Attempts</div>
                  <div className="text-2xl font-semibold mb-0">{assessmentDetails.total_attempts || 0}</div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="text-gray-500 mb-1 text-sm">Average Score</div>
                  <div className="text-2xl font-semibold mb-0">{assessmentDetails.average_score || 0}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assessment Information */}
      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 h-full">
            <div className="p-4">
              <h5 className="text-xl font-semibold mb-3">Assessment Information</h5>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-gray-500 text-sm">Duration</div>
                  <div className="font-medium">{assessmentDetails.duration ? `${assessmentDetails.duration} minutes` : 'Not specified'}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-sm">Pass Percentage</div>
                  <div className="font-medium">{assessmentDetails.pass_percentage || 0}%</div>
                </div>
                <div>
                  <div className="text-gray-500 text-sm">Points</div>
                  <div className="font-medium">{assessmentDetails.earn_point || 0}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-sm">Status</div>
                  <div className="font-medium">
                    <span className="text-primary bg-gray-50 border border-primary px-2 py-1 rounded text-sm">
                      {assessmentDetails.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 h-full">
            <div className="p-4">
              <h5 className="text-xl font-semibold mb-3">Course Information</h5>
              <div className="text-gray-500 text-sm">Assessment Name</div>
              <div className="font-medium mb-3">{assessmentDetails.assessment_name || 'N/A'}</div>
              <div className="text-gray-500 text-sm mt-3">Course Name</div>
              <div className="font-medium mb-3">{assessmentDetails.course_name || 'N/A'}</div>
              <div className="text-gray-500 text-sm mt-3">Created Date</div>
              <div className="font-medium">
                {assessmentDetails.createdAt
                  ? new Date(assessmentDetails.createdAt).toLocaleDateString()
                  : 'N/A'
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Attempts */}
      <div>
        <div className="w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="p-4">
              <h5 className="text-xl font-semibold mb-4">Recent Attempts</h5>
              {recentAttempts.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No attempts found for this assessment
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="bg-gray-50 font-medium py-3 px-4 text-left">User</th>
                        <th className="bg-gray-50 font-medium py-3 px-4 text-left">Score</th>
                        <th className="bg-gray-50 font-medium py-3 px-4 text-left">Status</th>
                        <th className="bg-gray-50 font-medium py-3 px-4 text-left">Last Attempt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentAttempts.map((attempt, index) => (
                        <tr key={index} className="border-b">
                          <td className="py-3 align-middle px-4">
                            <div className="flex items-center gap-2">
                              <div className="bg-primary rounded-full flex items-center justify-center text-white text-sm"
                                style={{ width: '32px', height: '32px' }}>
                                {attempt.user_name?.charAt(0)?.toUpperCase() || 'U'}
                              </div>
                              <div>
                                <div className="font-medium">{attempt.user_name || 'Unknown User'}</div>
                                <div className="text-gray-500 text-sm">{attempt.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 align-middle px-4">
                            <div className="font-medium">{attempt.score_percentage}%</div>
                            <div className="text-gray-500 text-sm">
                              {attempt.correct_answers}/{attempt.total_questions} correct
                            </div>
                          </td>
                          <td className="py-3 align-middle px-4">
                            <span className={`px-2 py-1 rounded text-sm ${attempt.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {attempt.passed ? 'Passed' : 'Failed'}
                            </span>
                          </td>
                          <td className="py-3 align-middle px-4">
                            <div className="text-gray-500">
                              {attempt.last_attempt_date
                                ? new Date(attempt.last_attempt_date).toLocaleDateString()
                                : 'N/A'
                              }
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
      </div>
    </div>

      </div>
      {/* Analytics Modal */}
      {showAnalyticsModal && analyticsData && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-xl shadow-xl max-w-[95vw] w-[1400px] mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h5 className="text-xl font-semibold flex items-center">
                <Icon icon="mdi:chart-line" className="mr-2" width="24" height="24" />
                Assessment Analytics
                {!analyticsData.geminiAvailable && (
                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-sm ml-2">Fallback Analysis</span>
                )}
              </h5>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                onClick={() => setShowAnalyticsModal(false)}
                aria-label="Close"
              >&times;</button>
            </div>
            <div className="p-6">
              <div>
                <div className="w-full">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <Icon icon="mdi:information" className="mr-2 mt-1" width="18" />
                      <div>
                        <strong>Assessment:</strong> {assessmentDetails?.assessment_name}
                        <br />
                        <small className="text-gray-500">
                          {analyticsData.geminiAvailable
                            ? 'AI-powered analysis using Gemini'
                            : 'Fallback analysis (Gemini API unavailable)'
                          }
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                    <div className="p-4">
                      <h6 className="text-lg font-semibold mb-3">Analysis Results</h6>
                        <div
                          className="analysis-content"
                          style={{
                            maxHeight: '600px',
                            overflowY: 'auto',
                            fontSize: '14px',
                            lineHeight: '1.6',
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef'
                          }}
                        >
                          {(() => {
                            // Create a mapping of database IDs to sequential numbers
                            const questionIdMap = {};
                            const questionIds = Object.keys(analyticsData.stats || {});
                            questionIds.forEach((dbId, index) => {
                              questionIdMap[dbId] = index + 1;
                            });

                            // Replace Q30, Q31, etc. with Q1, Q2, etc. in the analysis text
                            let processedAnalysis = analyticsData.analysis;
                            Object.entries(questionIdMap).forEach(([dbId, seqNumber]) => {
                              const regex = new RegExp(`Q${dbId}\\b`, 'g');
                              processedAnalysis = processedAnalysis.replace(regex, `Q${seqNumber}`);
                            });

                            return processedAnalysis.split('\n').map((line, index) => {
                            // Handle headers
                            if (line.startsWith('## ')) {
                              return (
                                <h4 key={index} className="text-primary font-bold mb-3 mt-4" style={{ fontSize: '18px' }}>
                                  {line.replace('## ', '')}
                                </h4>
                              );
                            }
                            if (line.startsWith('**') && line.endsWith('**')) {
                              return (
                                <h5 key={index} className="text-gray-900 font-bold mb-2 mt-3" style={{ fontSize: '16px' }}>
                                  {line.replace(/\*\*/g, '')}
                                </h5>
                              );
                            }

                            // Handle bullet points
                            if (line.trim().startsWith('* **')) {
                              const content = line.replace(/^\*\s\*\*(.*?)\*\*:\s*(.*)/, '$1: $2');
                              return (
                                <div key={index} className="mb-2">
                                  <strong className="text-green-600">{content.split(':')[0]}:</strong>
                                  <span className="ml-1">{content.split(':').slice(1).join(':')}</span>
                                </div>
                              );
                            }

                            // Handle regular bullet points
                            if (line.trim().startsWith('* ')) {
                              return (
                                <div key={index} className="mb-1 ml-3">
                                  <span className="text-gray-500">•</span> {line.replace(/^\*\s/, '')}
                                </div>
                              );
                            }

                            // Handle numbered lists
                            if (line.trim().match(/^\d+\.\s/)) {
                              return (
                                <div key={index} className="mb-1 ml-3">
                                  <span className="text-gray-500">{line.match(/^\d+\./)[0]}</span> {line.replace(/^\d+\.\s/, '')}
                                </div>
                              );
                            }

                            // Handle bold text
                            if (line.includes('**')) {
                              const parts = line.split(/(\*\*.*?\*\*)/g);
                              return (
                                <div key={index} className="mb-2">
                                  {parts.map((part, partIndex) => {
                                    if (part.startsWith('**') && part.endsWith('**')) {
                                      return <strong key={partIndex} className="text-primary">{part.replace(/\*\*/g, '')}</strong>;
                                    }
                                    return <span key={partIndex}>{part}</span>;
                                  })}
                                </div>
                              );
                            }

                            // Handle empty lines
                            if (line.trim() === '') {
                              return <div key={index} className="mb-2"></div>;
                            }

                            // Regular text
                            return (
                              <div key={index} className="mb-2">
                                {line}
                              </div>
                            );
                          });
                          })()}
                        </div>
                      </div>
                    </div>

                    {analyticsData.stats && (
                      <div className="bg-white rounded-lg shadow-sm border border-gray-100 mt-3">
                        <div className="p-4">
                          <h6 className="text-lg font-semibold mb-3">Question Statistics</h6>
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="py-2 px-3 text-left" style={{ width: '15%' }}>Question ID</th>
                                  <th className="py-2 px-3 text-left" style={{ width: '20%' }}>Total Attempts</th>
                                  <th className="py-2 px-3 text-left" style={{ width: '20%' }}>Correct</th>
                                  <th className="py-2 px-3 text-left" style={{ width: '20%' }}>Wrong</th>
                                  <th className="py-2 px-3 text-left" style={{ width: '25%' }}>Success Rate</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(analyticsData.stats).map(([questionId, stats], index) => {
                                  const successRate = stats.total > 0 ? (stats.correct / stats.total) * 100 : 0;
                                  const questionNumber = index + 1; // Sequential numbering starting from 1
                                  return (
                                    <tr key={questionId} className="border-b hover:bg-gray-50">
                                      <td className="py-2 px-3 font-medium">Q{questionNumber}</td>
                                      <td className="py-2 px-3">
                                        <span className="bg-gray-50 border border-gray-400 text-gray-700 px-2 py-1 rounded text-sm">{stats.total}</span>
                                      </td>
                                      <td className="py-2 px-3">
                                        <span className="bg-gray-50 border border-green-500 text-green-700 px-2 py-1 rounded text-sm">{stats.correct}</span>
                                      </td>
                                      <td className="py-2 px-3">
                                        <span className="bg-gray-50 border border-red-500 text-red-700 px-2 py-1 rounded text-sm">{stats.wrong}</span>
                                      </td>
                                      <td className="py-2 px-3">
                                        <div className="flex items-center">
                                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex-grow mr-2">
                                            <div
                                              className={`h-full transition-all ${successRate >= 80 ? 'bg-green-500' :
                                                  successRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                                }`}
                                              style={{ width: `${successRate}%` }}
                                            ></div>
                                          </div>
                                          <span className={`bg-gray-50 border px-2 py-1 rounded text-sm ${successRate >= 80 ? 'border-green-500 text-green-700' :
                                              successRate >= 60 ? 'border-yellow-500 text-yellow-700' : 'border-red-500 text-red-700'
                                            }`} style={{ minWidth: '60px' }}>
                                            {successRate.toFixed(1)}%
                                          </span>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="p-6 border-t flex justify-end gap-2">
                <button
                  type="button"
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg flex items-center gap-2"
                  onClick={() => setShowAnalyticsModal(false)}
                >
                  <Icon icon="mdi:close" className="mr-1" width="16" />
                  Close
                </button>
              </div>
            </div>
          </div>
      )}
    </>
  );
}

export default CourseAnalyticsAssessmentDetails;
